/**
 * Logs an error to New Relic and console. If noticeError crashes (e.g. due to
 * @babel/polyfill + NR agent conflict), falls back to reporting a plain Error message.
 *
 * @param {Error|string} err - The error to report
 * @param {Object} [customAttributes={}] - Additional attributes to attach to the NR error
 * @param {boolean} [notifyNR=true] - Whether to send the error to New Relic
 */
export function error(err, customAttributes = {}, notifyNR = true) {
	if (notifyNR) {
		try {
			const newrelic = window.newrelic || window.NREUM;
			const userId = window?.ui?.getCookie('LAU');

			const outgoingError = typeof err === 'string' ? new Error(err) : err;
			newrelic?.noticeError?.(outgoingError, { sourceApp: 'hintsui-eval', level: 'error', userId, ...customAttributes });
		} catch (nrError) {
			// Note: If noticeError crashes, report a safe plain Error with the original message
			try {
				const newrelic = window.newrelic || window.NREUM;
				const message = err?.message || err?.toString?.() || 'Unknown error';
				newrelic?.noticeError?.(new Error(message), { sourceApp: 'hintsui-eval', level: 'error', fallback: true });
			} catch (_) {
				// Note: Give up reporting to NR
			}
		}
	}

	console.error(err); // eslint-disable-line no-console
}

/**
 * Logs a warning message to New Relic.
 * Note: Info & Warn logs needs to be enabled for each page level under Browser > Application settings > Collect logs manually > Select 'Info'
 *
 * @param {string} message - The warning message
 * @param {Object} [customAttributes={}] - Additional attributes to attach
 */
export function warn(message, customAttributes = {}) {
	const userId = window?.ui?.getCookie('LAU');
	const newrelic = window.newrelic || window.NREUM;
	const attrs = { sourceApp: 'hintsui-eval', userId, ...customAttributes };
	newrelic?.log(message, { level: 'warn', customAttributes: attrs });
}

/**
 * Logs an info message to New Relic.
 *
 * @param {string} message - The info message
 * @param {Object} [customAttributes={}] - Additional attributes to attach
 */
export function info(message, customAttributes = {}) {
	const userId = window?.ui?.getCookie('LAU');
	const newrelic = window.newrelic || window.NREUM;
	const attrs = { sourceApp: 'hintsui-eval', userId, ...customAttributes };
	newrelic?.log(message, { level: 'info', customAttributes: attrs });
}

export default { error, warn, info };
